import { useCallback, useEffect, useState } from "react";
import { Check, Download, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from "@/lib/api";

const STATUSES = ["new", "in_progress", "handled", "archived"];

export default function AdminInquiries({ client }) {
    const [items, setItems] = useState(null);
    const [filter, setFilter] = useState("open");

    const load = useCallback(() => {
        client.get("/admin/inquiries").then(({ data }) => setItems(data)).catch(() => toast.error("Failed to load inquiries"));
    }, [client]);
    useEffect(load, [load]);

    const setStatus = async (id, status) => {
        try {
            await client.patch(`/admin/inquiries/${id}`, { status });
            setItems((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
        } catch { toast.error("Update failed"); }
    };

    const download = async (fid) => {
        try {
            const { data } = await client.post(`/admin/files/${fid}/link`);
            window.open(`${BACKEND_URL}${data.url}`, "_blank", "noopener");
        } catch { toast.error("Could not create download link"); }
    };

    if (!items) return <Loader2 className="h-6 w-6 animate-spin text-forest" />;
    const shown = items.filter((i) => filter === "all" || (filter === "open" ? !["handled", "archived"].includes(i.status) : i.status === filter));

    return (
        <div data-testid="admin-inquiries">
            <div className="flex flex-wrap items-center gap-2 mb-6">
                {["open", "all", ...STATUSES].map((f) => (
                    <button key={f} type="button" onClick={() => setFilter(f)} data-testid={`admin-inq-filter-${f}`}
                            className={`rounded-full border px-4 py-1.5 text-sm ${filter === f ? "bg-forest text-cream border-forest" : "border-hairline"}`}>{f.replace("_", " ")}</button>
                ))}
                <button type="button" onClick={load} className="ml-auto inline-flex items-center gap-2 text-sm text-muted_text hover:text-forest" data-testid="admin-inq-refresh"><RefreshCw className="h-4 w-4" /> Refresh</button>
            </div>
            {shown.length === 0 && <p className="text-muted_text">No inquiries.</p>}
            <ul className="space-y-4">
                {shown.map((i) => (
                    <li key={i.id} data-testid={`admin-inquiry-${i.id}`} className="rounded-2xl bg-white/60 border border-hairline p-6">
                        <div className="flex flex-wrap items-start justify-between gap-4">
                            <div>
                                <p className="mono-label text-moss">{i.inquiry_type}{i.project_slug ? ` · ${i.project_slug}` : ""}</p>
                                <p className="font-serif text-2xl text-forest mt-1">{i.name}{i.company ? <span className="text-muted_text text-lg"> — {i.company}</span> : null}</p>
                                <p className="text-sm text-muted_text mt-1"><a className="underline" href={`mailto:${i.email}`}>{i.email}</a>{i.phone ? ` · ${i.phone}` : ""} · {new Date(i.created_at).toLocaleString("en-GB")}</p>
                            </div>
                            <select value={i.status} onChange={(e) => setStatus(i.id, e.target.value)} data-testid={`admin-inquiry-status-${i.id}`}
                                    className="rounded-full border border-hairline bg-cream px-4 py-2 text-sm">
                                {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                            </select>
                        </div>
                        <p className="mt-4 whitespace-pre-wrap text-sm text-ink leading-relaxed">{i.message}</p>
                        {i.attachment_files?.length > 0 && (
                            <div className="mt-4 flex flex-wrap gap-2">
                                {i.attachment_files.map((f) => (
                                    <button type="button" key={f.id} onClick={() => download(f.id)}
                                       className="inline-flex items-center gap-2 rounded-full bg-surface px-3 py-1.5 text-xs text-forest hover:bg-sage/40" data-testid={`admin-attachment-${f.id}`}>
                                        <Download className="h-3.5 w-3.5" /> {f.filename}
                                    </button>
                                ))}
                            </div>
                        )}
                        {i.status === "new" && (
                            <button type="button" onClick={() => setStatus(i.id, "handled")} className="mt-4 inline-flex items-center gap-2 text-sm text-forest hover:text-moss" data-testid={`admin-inquiry-handled-${i.id}`}>
                                <Check className="h-4 w-4" /> Mark handled
                            </button>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
}
